import React, { useState } from 'react';
import {
  Bug,
  X,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  TriangleAlert,
  Info,
  Search,
  Trash2,
  Copy,
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { errorLoggingService, ErrorLog } from '../../services/errorLoggingService';

type FilterType = 'all' | 'error' | 'warning' | 'info';

const ErrorDebugPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');

  const handleOpen = () => {
    setOpen(true);
    setLogs(errorLoggingService.getStoredLogs());
  };

  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all error logs?')) {
      errorLoggingService.clearStoredLogs();
      setLogs([]);
    }
  };

  const toggleExpanded = (logId: string) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const copyToClipboard = (log: ErrorLog) => {
    const text = JSON.stringify(log, null, 2);
    navigator.clipboard.writeText(text);
  };

  const getIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <CircleAlert className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <TriangleAlert className="h-4 w-4 text-amber-600" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-600" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filterType !== 'all' && log.severity !== filterType) {
      return false;
    }
    if (filter) {
      const searchLower = filter.toLowerCase();
      return (
        log.message.toLowerCase().includes(searchLower) ||
        log.type.toLowerCase().includes(searchLower) ||
        (log.correlationId && log.correlationId.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  const errorCount = logs.filter(l => l.severity === 'error').length;
  const warningCount = logs.filter(l => l.severity === 'warning').length;

  // Only show in development
  if (!import.meta.env.DEV) {
    return null;
  }

  const FilterPill = ({ type, label, active }: { type: FilterType; label: string; active: boolean }) => (
    <button
      onClick={() => setFilterType(type)}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-transparent text-foreground hover:bg-muted'
      )}
    >
      {label}
    </button>
  );

  return (
    <>
      <button
        onClick={handleOpen}
        aria-label="Open error debug panel"
        className="fixed bottom-4 right-4 z-[9999] flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-secondary-foreground shadow-lg hover:brightness-95"
      >
        <span className="relative">
          <Bug className="h-5 w-5" />
          {errorCount > 0 && (
            <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {errorCount}
            </span>
          )}
        </span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[400px] max-w-full overflow-y-auto p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold">Error Debug Panel</h2>
          </div>

          <div className="mb-4 flex gap-1">
            <FilterPill type="all" label={`All (${logs.length})`} active={filterType === 'all'} />
            <FilterPill type="error" label={`Errors (${errorCount})`} active={filterType === 'error'} />
            <FilterPill type="warning" label={`Warnings (${warningCount})`} active={filterType === 'warning'} />
          </div>

          <div className="relative mb-4">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pl-8"
            />
          </div>

          <Button
            variant="outline"
            onClick={handleClearLogs}
            className="mb-4 w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 />
            Clear All Logs
          </Button>

          <hr className="border-border" />

          <div className="mt-4 space-y-2">
            {filteredLogs.length === 0 ? (
              <div className="px-1 py-2">
                <p className="text-sm">No logs found</p>
                <p className="text-xs text-muted-foreground">Errors will appear here when they occur</p>
              </div>
            ) : (
              filteredLogs.slice().reverse().map((log, index) => {
                const logId = `${log.timestamp}-${index}`;
                const expanded = expandedLog === logId;
                return (
                  <div key={logId} className="rounded-md border border-border p-1 shadow-sm">
                    <button
                      onClick={() => toggleExpanded(logId)}
                      className="flex w-full cursor-pointer items-center gap-2 p-1 text-left"
                    >
                      <span className="shrink-0">{getIcon(log.severity)}</span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm">{log.message}</span>
                        <span className="block text-xs text-muted-foreground">
                          {new Date(log.timestamp).toLocaleTimeString()} - {log.type}
                        </span>
                      </span>
                      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>

                    {expanded && (
                      <div className="rounded bg-gray-50 p-2">
                        <div className="text-xs">
                          <strong>URL:</strong> {log.context.url}
                        </div>
                        {log.correlationId && (
                          <div className="text-xs">
                            <strong>Correlation ID:</strong> {log.correlationId}
                          </div>
                        )}
                        {log.context.userId && (
                          <div className="text-xs">
                            <strong>User ID:</strong> {log.context.userId}
                          </div>
                        )}
                        {log.stack && (
                          <div className="mt-1">
                            <div className="text-xs">
                              <strong>Stack Trace:</strong>
                            </div>
                            <pre className="max-h-52 overflow-auto rounded bg-gray-100 p-1 text-[0.7rem]">
                              {log.stack}
                            </pre>
                          </div>
                        )}
                        {log.metadata && (
                          <div className="mt-1">
                            <div className="text-xs">
                              <strong>Metadata:</strong>
                            </div>
                            <pre className="overflow-auto rounded bg-gray-100 p-1 text-[0.7rem]">
                              {JSON.stringify(log.metadata, null, 2)}
                            </pre>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(log)}
                          className="mt-1"
                        >
                          <Copy />
                          Copy to Clipboard
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ErrorDebugPanel;
